import App from "./components/App";
import { HashRouter } from "react-router-dom";
import { FluentProvider, webLightTheme, webDarkTheme } from "@fluentui/react-components";
import { FC, useEffect, useState } from "react";
import { WalterProvider } from "./providers/walter";
import * as Sentry from "@sentry/react";
import { officeInitialized } from "./hooks/useOfficeInitialized";
import { captureErrorInfo } from "@src/taskpane/helpers/errorHandler";
import debug from "@src/lib/debug";

export interface OnOfficeReadyInfo {
  platform: Office.PlatformType;
  host: Office.HostType;
}

export type OnOfficeReadyCallback = (info: OnOfficeReadyInfo) => any;
export type OnOfficeReady = (callback?: OnOfficeReadyCallback) => Promise<OnOfficeReadyInfo>;
interface Props {
  onOfficeReady: OnOfficeReady;
}

export const AppShell: FC<Props> = ({ onOfficeReady }) => {
  // Switch themes based on system theme
  const isDarkMode = window.matchMedia("(prefers-color-scheme: dark)");
  const [systemTheme, setSystemTheme] = useState(isDarkMode.matches ? webDarkTheme : webLightTheme);

  // Handle Office.js initialization
  useEffect(() => {
    void onOfficeReady(({ platform }) => {
      debug.log(() => "Office ready, platform:", platform);
      Sentry.setTag("platform", platform);
      officeInitialized();
    }).catch((error) => {
      debug.error("Office initialization failed:", error);
      captureErrorInfo(error);
    });

    const handleChange = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? webDarkTheme : webLightTheme);
    isDarkMode.addEventListener("change", handleChange);

    return () => isDarkMode.removeEventListener("change", handleChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isDarkMode won't change
  }, []);

  return (
    <FluentProvider theme={systemTheme}>
      <HashRouter>
        <WalterProvider>
          <App />
        </WalterProvider>
      </HashRouter>
    </FluentProvider>
  );
};
